import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import api from 'state/axios';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography, 
  Tooltip, 
  IconButton, 
  Chip, 
  CircularProgress 
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';
import DataGridComponent from 'components/DataGridComponent';


const CATEGORY_LABELS = {
  initial: 'Initial',
  recurrent: 'Recurrent',
  upgrade: 'Upgrade',
  special: 'Special',
  other: 'Other',
};

const VALIDITY_TYPE_LABELS = {
  1: 'Days',
  2: 'Months ending at end',
  3: 'Months ending on day',
  4: 'Never Expires',
  5: 'Months ending at end + 1 day',
  6: 'Calendar Year',
  7: 'Months ending on day -1 day',
};

const TrainingSubjects = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "TrainingSubjects", "create", false);
  const canEdit = hasPermission(user?.permissions, "TrainingSubjects", "update", false);
  const canDelete = hasPermission(user?.permissions, "TrainingSubjects", "delete", false);

  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  const fetchSubjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/trainingsubjects');
      setSubjects(res.data);
    } catch (error) {
      setError(error);
      toast.error(error?.response?.data?.message || "Failed to load training subjects!");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSubjects(); }, [fetchSubjects]);

  const executeDelete = useCallback(async () => {
    try {
      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/trainingsubjects/delete/${idToDelete}`);
        setSubjects((prev) => prev.filter((item) => item._id !== idToDelete));
        toast.success('Subject deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, subjects);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const res = await api.patch('/trainingsubjects/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setSubjects((prev) => prev.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed!");
    }
  }, [deleteType, idToDelete, rowSelectionModel, subjects]);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, subjects);
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => isMobile ? { width: 60 } : { flex: 1 })(),
    },
    {
      field: "subjectCode",
      headerName: "Code",
      ...(() => isMobile ? { width: 100 } : { flex: 1.5 })(),
    },
    {
      field: "subjectName",
      headerName: "Subject Name",
      ...(() => isMobile ? { width: 180 } : { flex: 3 })(),
    },
    {
      field: "category",
      headerName: "Category",
      ...(() => isMobile ? { width: 120 } : { flex: 1.5 })(),
      renderCell: ({ value }) => <Chip label={CATEGORY_LABELS[value] || value} size="small" />,
    },
    {
      field: "validityPeriod",
      headerName: "ValidityPeriod",
      ...(() => isMobile ? { width: 110 } : { flex: 1.5 })(),
      renderCell: ({ row }) => {
        const type = row.validityType ?? 3;
        if (type === 4) return <Chip label="Never" size="small" variant="outlined" />;
        const unit = type === 1 ? 'd' : 'mo';
        return (
          <Tooltip title={VALIDITY_TYPE_LABELS[type] || ''} arrow>
            <span>{row.validityPeriod != null ? `${row.validityPeriod} ${unit}` : '-'}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "alertBefore",
      headerName: "AlertBefore",
      ...(() => isMobile ? { width: 90 } : { flex: 1 })(),
      renderCell: ({ row }) => {
        if (!row.alertBefore) return '-';
        const unit = { day: 'd', week: 'wk', month: 'mo', year: 'yr' }[row.alertBeforeType] || 'mo';
        return `${row.alertBefore} ${unit}`;
      },
    },    
    {
      field: "isActive",
      headerName: "Active",
      ...(() => isMobile ? { width: 80 } : { flex: 1 })(),
      renderCell: ({ value }) => (
        <Chip label={value ? "Yes" : "No"} size="small" color={value ? "success" : "default"} />
      ),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {    
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => {
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ width: "100%", height: "100%" }}
            >
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    aria-label="Edit"
                    sx={{
                      color: theme.palette.primary.dark,
                      "&:hover": { color: theme.palette.green.deep },
                    }}
                    onClick={() => {
                      if (!canEdit) return;
                      navigate(`/trainingsubjects/${params.row._id}`)
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="Delete"
                    sx={{
                      color: theme.palette.primary.dark,
                      "&:hover": { color: theme.palette.green.deep },
                    }}
                    onClick={() => {
                      if (!canDelete) return;
                      handleDeleteClick(params.row._id);                  
                    }}
                  >
                    <DeleteForever />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ];
  }, [
    navigate, 
    theme.palette, 
    canEdit,
    canDelete,
  ]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Training Subjects"
          subtitle={`${loading ? 'Loading...' : 'List of Subjects'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Bulk delete" arrow>
              <span>
                <StyledButton
                  disabled={!canDelete || !isAnyRowSelected(rowSelectionModel, subjects)}
                  onClick={handleBulkDelete}
                >
                  <Delete />
                </StyledButton>
              </span>
            </Tooltip>
            <Tooltip title="Create" arrow>
              <span>
                <StyledButton
                  disabled={!canCreate}
                  onClick={() => navigate("/trainingsubjects/new")}
                >
                  <AddCircleOutline />
                </StyledButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
          sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <CardList
                rows={subjects}
                primaryField="subjectCode"
                secondaryField="subjectName"
                fields={[
                  { label: "Category", key: "category",
                    render: (row) => CATEGORY_LABELS[row.category] || row.category || null,
                  },
                  {
                    label: "Validity",
                    key: "validityPeriod",
                    render: (row) => {
                      const type = row.validityType ?? 3;
                      if (type === 4) return 'Never Expires';
                      const unit = type === 1 ? 'd' : 'mo';
                      return row.validityPeriod != null ? `${row.validityPeriod} ${unit}` : null;
                    },
                  },
                  {
                    label: "Alert",
                    key: "alertBefore",
                    render: (row) => {
                      if (!row.alertBefore) return null;
                      const unit = { day: 'd', week: 'wk', month: 'mo', year: 'yr' }[row.alertBeforeType] || 'mo';
                      return `${row.alertBefore} ${unit}`;
                    },
                  },
                  {
                    label: "Pass Score",
                    key: "passingScore",
                    render: (row) => row.passingScore != null ? `${row.passingScore}%` : null,
                  },
                  {
                    label: "Distinction",
                    key: "distinctionScore",
                    render: (row) => row.distinctionScore != null ? `${row.distinctionScore}%` : null,
                  },
                  { label: "Description", key: "description" },
                  {
                    label: "Ranks",
                    key: "applicableRanks",
                    render: (row) => row.applicableRanks?.length ? row.applicableRanks.join(', ') : null,
                  },
                  {
                    label: "AC Types",
                    key: "applicableAcTypes",
                    render: (row) => row.applicableAcTypes?.length ? row.applicableAcTypes.join(', ') : null,
                  },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/trainingsubjects/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={subjects}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  rowSelectionModel,
                  onRowSelectionModelChange: setRowSelectionModel,
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                }}
              />
            )}
            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default TrainingSubjects;
